import React, { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { ordersClient } from "../happyspace-restaurant-sdk";
import { Button } from "../components/ui";
import { 
  Loader2, 
  CheckCircle2, 
  Clock, 
  MapPin, 
  Phone, 
  ChefHat, 
  Package, 
  Truck,
  XCircle,
  RefreshCw
} from "lucide-react";
import { motion } from "framer-motion";

// Order status mapping with icons and colors
const STATUS_CONFIG: Record<string, { icon: any; label: string; color: string; bgColor: string }> = {
  pending: { 
    icon: Clock, 
    label: "Order Received", 
    color: "text-yellow-600", 
    bgColor: "bg-yellow-100" 
  },
  confirmed: { 
    icon: CheckCircle2, 
    label: "Order Confirmed", 
    color: "text-blue-600", 
    bgColor: "bg-blue-100" 
  },
  preparing: { 
    icon: ChefHat, 
    label: "Being Prepared", 
    color: "text-orange-600", 
    bgColor: "bg-orange-100" 
  },
  ready: { 
    icon: Package, 
    label: "Ready for Pickup", 
    color: "text-green-600", 
    bgColor: "bg-green-100" 
  },
  out_for_delivery: { 
    icon: Truck, 
    label: "Out for Delivery", 
    color: "text-purple-600", 
    bgColor: "bg-purple-100" 
  },
  delivered: { 
    icon: CheckCircle2, 
    label: "Delivered", 
    color: "text-green-600", 
    bgColor: "bg-green-100" 
  },
  completed: { 
    icon: CheckCircle2, 
    label: "Completed", 
    color: "text-green-600", 
    bgColor: "bg-green-100" 
  },
  cancelled: { 
    icon: XCircle, 
    label: "Cancelled", 
    color: "text-red-600", 
    bgColor: "bg-red-100" 
  },
};

export default function OrderStatusPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") || "";

  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  // Fetch order status
  const fetchOrderStatus = useCallback(async () => {
    if (!orderId) {
      setError("No order ID provided");
      setLoading(false);
      return;
    }

    // Try to get email from URL params or sessionStorage
    let customerEmail = email;
    if (!customerEmail) {
      const storedOrder = sessionStorage.getItem("lastOrder");
      if (storedOrder) {
        try {
          const parsed = JSON.parse(storedOrder);
          if (parsed.orderId === orderId) {
            customerEmail = parsed.email;
          }
        } catch (e) {}
      }
    }

    if (!customerEmail) {
      setError("Email is required to view order status. Please check your confirmation email for the correct link.");
      setLoading(false);
      return;
    }

    try {
      console.log("[OrderStatus] Fetching order:", orderId, "for email:", customerEmail);
      const response = await ordersClient.getOrderStatus({ 
        orderId, 
        email: customerEmail 
      });

      if (response.success && response.order) {
        setOrder(response.order);
        setError(null);
        setLastUpdated(new Date());
      } else {
        throw new Error("Order not found");
      }
    } catch (err: any) {
      console.error("[OrderStatus] Error:", err);
      
      // Try loading from sessionStorage as fallback for just-placed orders
      const storedOrder = sessionStorage.getItem("lastOrder");
      if (storedOrder) {
        try {
          const parsed = JSON.parse(storedOrder);
          if (parsed.orderId === orderId) {
            setOrder({
              _id: parsed.orderId,
              orderNumber: parsed.orderNumber,
              status: parsed.status || "pending",
              items: parsed.items || [],
              total: parsed.total,
              orderType: parsed.orderType,
              createdAt: parsed.createdAt,
            });
            setError(null);
            setLastUpdated(new Date());
            return;
          }
        } catch (e) {}
      }
      
      setError(err.message || "Could not fetch order status. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [orderId, email]);

  // Initial fetch
  useEffect(() => {
    fetchOrderStatus();
  }, [fetchOrderStatus]);

  // Poll for updates every 30 seconds for active orders
  useEffect(() => {
    if (!order || ["completed", "delivered", "cancelled"].includes(order.status)) {
      return;
    }

    const interval = setInterval(() => {
      fetchOrderStatus();
    }, 30000);

    return () => clearInterval(interval);
  }, [order, fetchOrderStatus]);

  // Get status configuration
  const statusConfig = order?.status ? STATUS_CONFIG[order.status] || STATUS_CONFIG.pending : STATUS_CONFIG.pending;
  const StatusIcon = statusConfig.icon;

  // Estimate time based on status
  const getEstimatedTime = () => {
    if (!order) return "Calculating...";
    
    switch (order.status) {
      case "pending":
      case "confirmed":
        return order.orderType === "delivery" ? "45-60 mins" : "15-20 mins";
      case "preparing":
        return order.orderType === "delivery" ? "30-45 mins" : "10-15 mins";
      case "ready":
        return "Ready now!";
      case "out_for_delivery":
        return "10-20 mins";
      case "delivered":
      case "completed":
        return "Complete";
      default:
        return "Calculating...";
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Loader2 className="w-10 h-10 animate-spin text-[#D97706]" />
        <p className="text-[#451A03] font-medium">Loading your order...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="container mx-auto py-20 px-4 max-w-2xl text-center">
        <div className="bg-red-50 rounded-2xl p-8 border border-red-200">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-[#451A03] mb-2">Could Not Load Order</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="flex gap-4 justify-center">
            <Button onClick={fetchOrderStatus} className="bg-[#D97706] hover:bg-[#B45309]">
              <RefreshCw className="w-4 h-4 mr-2" /> Try Again
            </Button>
            <Link to="/">
              <Button variant="outline">Return Home</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-12 px-4 max-w-2xl">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-[2rem] shadow-xl border border-orange-50 overflow-hidden"
      >
        {/* Header */}
        <div className="bg-[#451A03] p-10 text-center text-white">
          <div className={`w-20 h-20 ${statusConfig.bgColor} rounded-full flex items-center justify-center mx-auto mb-6`}>
            <StatusIcon className={`w-10 h-10 ${statusConfig.color}`} />
          </div>
          <h1 className="text-3xl font-bold mb-2">{statusConfig.label}</h1>
          <p className="text-orange-100/60">
            Order #{order?.orderNumber || order?._id?.slice(-8).toUpperCase()}
          </p>
        </div>

        {/* Order Details */}
        <div className="p-10 space-y-8">
          {/* Estimated Time */}
          <div className="flex items-center gap-6 p-6 bg-orange-50 rounded-2xl">
            <div className="bg-white p-4 rounded-xl shadow-sm">
              <Clock className="w-8 h-8 text-[#D97706]" />
            </div>
            <div>
              <p className="text-sm text-gray-500 uppercase tracking-wider font-bold">
                {order?.status === "completed" || order?.status === "delivered" 
                  ? "Order Complete" 
                  : "Estimated Time"}
              </p>
              <p className="text-2xl font-bold text-[#451A03]">{getEstimatedTime()}</p>
            </div>
          </div>

          {/* Order Items Summary */}
          {order?.items && order.items.length > 0 && (
            <div className="space-y-4">
              <h3 className="font-bold text-[#451A03]">Order Items</h3>
              <div className="space-y-2">
                {order.items.map((item: any, idx: number) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      {item.quantity}x {item.title || item.name}
                    </span>
                    <span className="font-medium">
                      ${((item.price || item.basePrice || 0) * (item.quantity || 1)).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
              {order.total && (
                <div className="flex justify-between pt-3 border-t border-gray-100 font-bold text-[#451A03]">
                  <span>Total</span>
                  <span>${Number(order.total).toFixed(2)}</span>
                </div>
              )}
            </div>
          )}

          {/* Location / Contact Info */}
          <div className="space-y-6">
            <div className="flex gap-4">
              <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-gray-500" />
              </div>
              <div>
                <h4 className="font-bold text-[#451A03]">
                  {order?.orderType === "delivery" ? "Delivery Address" : "Pickup Location"}
                </h4>
                <p className="text-gray-500">
                  {order?.orderType === "delivery" && order?.deliveryAddress 
                    ? order.deliveryAddress 
                    : "Dallas TX"}
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Phone className="w-5 h-5 text-gray-500" />
              </div>
              <div>
                <h4 className="font-bold text-[#451A03]">Need help?</h4>
                <p className="text-gray-500">Contact the restaurant directly</p>
              </div>
            </div>
          </div>

          {/* Refresh Button */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-100 text-sm text-gray-500">
            <span>Last updated: {lastUpdated.toLocaleTimeString()}</span>
            <button 
              onClick={fetchOrderStatus}
              className="flex items-center gap-1 text-[#D97706] hover:text-[#B45309]"
            >
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
          </div>

          {/* Return Home Button */}
          <div className="pt-4">
            <Link to="/">
              <Button className="w-full bg-[#D97706] hover:bg-[#B45309] py-6 rounded-xl font-bold">
                Return Home
              </Button>
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
}